import { hasPermission, serverDatabaseSchema, type ServerDatabase } from '@hearth/shared';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Check, Copy, Database, Eye, EyeOff, KeyRound, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { Confirm } from '@/components/confirm';
import { FormField } from '@/components/form-field';
import { QueryState } from '@/components/query-state';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ApiRequestError, apiRequest } from '@/lib/api';
import { errorMessage } from '@/lib/forms';
import { serverQuery } from '@/lib/server-queries';

export const Route = createFileRoute('/_app/server/$server/databases')({ component: DatabasesTab });

const listSchema = z.object({ data: z.array(serverDatabaseSchema), limit: z.number().int() });

function DatabasesTab() {
  const { server: id } = Route.useParams();
  const { data: server } = useSuspenseQuery(serverQuery(id));
  const queryClient = useQueryClient();
  const key = ['server', id, 'databases'];
  const list = useQuery({
    queryKey: key,
    queryFn: ({ signal }) =>
      apiRequest(`/api/client/servers/${id}/databases`, { schema: listSchema, signal }),
  });
  const [creating, setCreating] = useState(false);
  // A password just created or rotated is shown once in a dialog, whoever may read it later.
  const [revealed, setRevealed] = useState<ServerDatabase | null>(null);
  const can = (p: Parameters<typeof hasPermission>[1]) => hasPermission(server.permissions, p);
  const refresh = () => void queryClient.invalidateQueries({ queryKey: key });

  const rotate = useMutation({
    mutationFn: (d: ServerDatabase) =>
      apiRequest(`/api/client/servers/${id}/databases/${d.id}/rotate-password`, {
        method: 'POST',
        schema: serverDatabaseSchema,
      }),
    onSuccess: (d) => {
      setRevealed(d);
      refresh();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const remove = useMutation({
    mutationFn: (d: ServerDatabase) =>
      apiRequest(`/api/client/servers/${id}/databases/${d.id}`, {
        method: 'DELETE',
        schema: z.null(),
      }),
    onSuccess: () => {
      toast.success('Database deleted.');
      refresh();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-muted-foreground text-sm">
          {list.data
            ? `${list.data.data.length} of ${list.data.limit} databases used.`
            : 'MySQL databases for plugins and mods.'}
        </p>
        {can('database.create') && (
          <Button onClick={() => setCreating(true)} disabled={list.data?.limit === 0}>
            <Plus /> New database
          </Button>
        )}
      </div>
      <QueryState query={list}>
        {({ data, limit }) =>
          data.length === 0 ? (
            <Card className="bg-card/50 border-dashed shadow-none">
              <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
                <span className="bg-elevated text-muted-foreground grid size-11 place-items-center rounded-lg border">
                  <Database className="size-6" aria-hidden />
                </span>
                <p className="font-medium">
                  {limit === 0 ? 'Databases are turned off' : 'No databases yet'}
                </p>
                <p className="text-muted-foreground max-w-sm text-sm">
                  {limit === 0
                    ? 'An administrator hasn’t given this server any databases.'
                    : 'Create one for a plugin that needs MySQL.'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <ul className="grid gap-3">
              {data.map((d) => (
                <li key={d.id}>
                  <DatabaseCard
                    db={d}
                    canRotate={can('database.update')}
                    canDelete={can('database.delete')}
                    onRotate={() => rotate.mutate(d)}
                    onDelete={() => remove.mutate(d)}
                  />
                </li>
              ))}
            </ul>
          )
        }
      </QueryState>
      {creating && (
        <CreateDialog
          server={id}
          onClose={() => setCreating(false)}
          onCreated={(d) => {
            setCreating(false);
            setRevealed(d);
            refresh();
          }}
        />
      )}
      {revealed && <PasswordDialog db={revealed} onClose={() => setRevealed(null)} />}
    </div>
  );
}

function CopyButton({ value, label }: { value: string; label: string }) {
  const [done, setDone] = useState(false);
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon-sm"
      aria-label={`Copy ${label}`}
      onClick={() => {
        void navigator.clipboard.writeText(value).then(() => {
          setDone(true);
          window.setTimeout(() => setDone(false), 1500);
        });
      }}
    >
      {done ? <Check className="text-success" /> : <Copy />}
    </Button>
  );
}

function Detail({
  label,
  value,
  secret,
}: {
  label: string;
  value: string | null;
  secret?: boolean;
}) {
  const [shown, setShown] = useState(false);
  if (value === null)
    return (
      <div>
        <dt>{label}</dt>
        <dd className="text-muted-foreground">Hidden (you don’t have the permission to view it)</dd>
      </div>
    );
  return (
    <div className="min-w-0">
      <dt>{label}</dt>
      <dd className="text-foreground flex items-center gap-1 font-mono">
        <span className="truncate">{secret && !shown ? '••••••••••••' : value}</span>
        {secret && (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={shown ? `Hide ${label}` : `Show ${label}`}
            onClick={() => setShown(!shown)}
          >
            {shown ? <EyeOff /> : <Eye />}
          </Button>
        )}
        <CopyButton value={value} label={label.toLowerCase()} />
      </dd>
    </div>
  );
}

function DatabaseCard({
  db,
  canRotate,
  canDelete,
  onRotate,
  onDelete,
}: {
  db: ServerDatabase;
  canRotate: boolean;
  canDelete: boolean;
  onRotate: () => void;
  onDelete: () => void;
}) {
  return (
    <Card className="gap-3 px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-mono text-sm font-medium break-all">{db.name}</p>
        <div className="flex gap-2">
          {canRotate && (
            <Confirm
              title="Rotate the password?"
              description="The old password stops working immediately. Anything using it (plugins, config files) needs the new one."
              action="Rotate"
              onConfirm={onRotate}
            >
              <Button variant="outline" size="sm">
                <KeyRound /> Rotate password
              </Button>
            </Confirm>
          )}
          {canDelete && (
            <Confirm
              title={`Delete ${db.name}?`}
              description="All its tables and data are removed for good."
              action="Delete"
              destructive
              onConfirm={onDelete}
            >
              <Button variant="outline-destructive" size="icon" aria-label={`Delete ${db.name}`}>
                <Trash2 />
              </Button>
            </Confirm>
          )}
        </div>
      </div>
      <dl className="text-muted-foreground grid gap-x-6 gap-y-2 text-xs sm:grid-cols-2 lg:grid-cols-4">
        <Detail label="Host" value={`${db.host.address}:${db.host.port}`} />
        <Detail label="Username" value={db.username} />
        <Detail label="Password" value={db.password} secret />
        <Detail label="Allowed from" value={db.remote === '%' ? 'Anywhere (%)' : db.remote} />
      </dl>
    </Card>
  );
}

function CreateDialog({
  server,
  onClose,
  onCreated,
}: {
  server: string;
  onClose: () => void;
  onCreated: (d: ServerDatabase) => void;
}) {
  const [name, setName] = useState('');
  const [remote, setRemote] = useState('%');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const create = useMutation({
    mutationFn: () =>
      apiRequest(`/api/client/servers/${server}/databases`, {
        method: 'POST',
        body: { name, remote },
        schema: serverDatabaseSchema,
      }),
    onSuccess: onCreated,
    onError: (e) => {
      if (e instanceof ApiRequestError && e.body?.fields)
        setErrors(
          Object.fromEntries(
            Object.entries(e.body.fields).map(([k, v]) => [k.replace('body.', ''), v[0] ?? '']),
          ),
        );
      else toast.error(errorMessage(e));
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New database</DialogTitle>
          <DialogDescription>
            A database and a user that can only use it are created for you.
          </DialogDescription>
        </DialogHeader>
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            setErrors({});
            create.mutate();
          }}
        >
          <FormField
            label="Name"
            description="Letters, numbers and underscores. It is added after a prefix."
            {...(errors.name ? { error: errors.name } : {})}
          >
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={24}
              required
              autoFocus
            />
          </FormField>
          <FormField
            label="Allowed from"
            description="Use % for anywhere, or an address such as 10.0.0.%."
            {...(errors.remote ? { error: errors.remote } : {})}
          >
            <Input
              value={remote}
              onChange={(e) => setRemote(e.target.value)}
              className="font-mono"
            />
          </FormField>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending || !name}>
              {create.isPending ? 'Creating…' : 'Create database'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PasswordDialog({ db, onClose }: { db: ServerDatabase; onClose: () => void }) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Connection details</DialogTitle>
          <DialogDescription>
            Copy the password now.{' '}
            {db.password ? 'It is shown here because you just created or changed it.' : ''}
          </DialogDescription>
        </DialogHeader>
        <dl className="text-muted-foreground grid gap-3 text-xs">
          <Detail label="Host" value={`${db.host.address}:${db.host.port}`} />
          <Detail label="Database" value={db.name} />
          <Detail label="Username" value={db.username} />
          <Detail label="Password" value={db.password} secret />
        </dl>
        <DialogFooter>
          <Button onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
